import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

// The chat panel is fetched the first time someone opens it, not on every page load.
const AiAssistantPanel = lazy(() => import("@/components/AiAssistantPanel"));

type Ctx = {
  open: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
};

const AiAssistantContext = createContext<Ctx | null>(null);

export const AiAssistantProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  // Stays mounted after the first open so the closing animation and the
  // conversation survive, exactly as before.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { if (open) setLoaded(true); }, [open]);

  const openAssistant = useCallback(() => setOpen(true), []);
  const closeAssistant = useCallback(() => setOpen(false), []);
  const toggleAssistant = useCallback(() => setOpen((v) => !v), []);

  return (
    <AiAssistantContext.Provider value={{ open, openAssistant, closeAssistant, toggleAssistant }}>
      {children}
      {(open || loaded) && (
        <Suspense fallback={null}>
          <AiAssistantPanel open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </AiAssistantContext.Provider>
  );
};

export const useAiAssistant = () => {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) throw new Error("useAiAssistant must be used inside <AiAssistantProvider>");
  return ctx;
};
